import { fetchJson } from './util'
import { NcCachedCompetition, NcSetupSelection, parseYearFromCompName } from './ncCache'
import { fetchDivisions, fetchDivisionsAndTeams, isSelectableNetballConnectTeamName } from './ncApi'

export const NETBALL_CONNECT_FIXTURE_URL = 'https://registration.netballconnect.com/liveScoreSeasonFixture'

export type NcResolvedResult = {
	selection: NcSetupSelection
	competitionData: NcCachedCompetition
}

type ParsedNetballConnectFixtureUrl = {
	competitionUniqueKey: string
	organisationKey?: string
	divisionId?: number
	teamId: number
}

function cleanNcText(value: string | number | null | undefined) {
	return String(value ?? '').replace(/\s+/g, ' ').trim()
}

function parseNetballConnectFixtureUrl(value: string): ParsedNetballConnectFixtureUrl {
	let url: URL

	try {
		url = new URL(value.trim())
	} catch (err) {
		throw new Error('Error: URL seems incomplete.')
	}

	if (!url.hostname.endsWith('netballconnect.com')) {
		throw new Error('Error: That does not look like a NetballConnect URL.')
	}

	const competitionUniqueKey = cleanNcText(
		url.searchParams.get('competitionUniqueKey') || url.searchParams.get('competitionKey'),
	)
	const teamId = Number(url.searchParams.get('teamId'))
	const organisationKey = cleanNcText(url.searchParams.get('organisationKey')) || undefined

	if (!competitionUniqueKey) {
		throw new Error('Error: URL is missing competition details.')
	}

	if (!teamId || Number.isNaN(teamId)) {
		throw new Error('Error: URL is missing team details.')
	}

	const divisionParam = cleanNcText(url.searchParams.get('divisionId'))
	const divisionId = divisionParam && divisionParam !== 'All' ? Number(divisionParam) : undefined

	return {
		competitionUniqueKey,
		organisationKey,
		divisionId: Number.isNaN(divisionId) ? undefined : divisionId,
		teamId,
	}
}

export async function resolveNetballConnectFixtureUrl(
	value: string,
	fallbackYear?: number,
): Promise<NcResolvedResult> {
	const parsed = parseNetballConnectFixtureUrl(value)

	// Fetch divisions (public) + optionally organisations
	const [divisions, organisations] = await Promise.all([
		fetchDivisions(parsed.competitionUniqueKey),
		parsed.organisationKey
			? fetchJson<NetballConnectOrganizations>('https://api-netball.squadi.com/users/api/organisations/all')
			: Promise.resolve(undefined),
	])

	if (!divisions.length) {
		throw new Error('No public divisions were found for that NetballConnect link.')
	}

	const [firstDivision] = divisions
	const competition = firstDivision?.competition

	if (!competition?.id || !competition?.name) {
		throw new Error('Could not resolve the competition from that link.')
	}

	const compName = cleanNcText(competition.name) || 'NetballConnect competition'
	const competitionKey = competition.uniqueKey || parsed.competitionUniqueKey

	// Fetch full divisions + teams for the entire competition (for cache hydration)
	const { divisions: cachedDivisions, teams: flatTeams } = await fetchDivisionsAndTeams(
		competitionKey,
		competition.id,
		compName,
		divisions,
	)

	// Find the selected team in the flat team list
	const selectedFlat = flatTeams.find(t => t.teamId === parsed.teamId)

	if (!selectedFlat || !isSelectableNetballConnectTeamName(selectedFlat.teamName)) {
		throw new Error(
			'That NetballConnect URL points to a team that is not valid for this competition. Please re-select your team in NetballConnect and copy the URL again.',
		)
	}

	// If not found in matches/ladder, find via division match
	const matchingDivision = divisions.find(d =>
		d.id === parsed.divisionId || d.id === selectedFlat?.divId,
	) || firstDivision

	// Resolve org name
	const orgMatch = organisations?.find(o => o.organisationUniqueKey === parsed.organisationKey)
	const orgKey = parsed.organisationKey || ''
	const orgName = cleanNcText(orgMatch?.name) || compName

	// Keep this cache year derivation aligned with firestore.rules if the allowed range ever changes.
	const year = parseYearFromCompName(compName) ?? fallbackYear ?? new Date().getFullYear()

	const selection: NcSetupSelection = {
		orgKey,
		orgName,
		competitionKey,
		competitionId: competition.id,
		compName,
		divId: selectedFlat.divId || matchingDivision?.id || 0,
		divName: cleanNcText(selectedFlat.divName || matchingDivision?.name || matchingDivision?.divisionName || 'Selected division'),
		teamId: parsed.teamId,
		teamName: cleanNcText(selectedFlat.teamName),
		logoUrl: selectedFlat.logoUrl,
	}

	const competitionData: NcCachedCompetition = {
		competitionId: competition.id,
		compName,
		year,
		updatedAt: Date.now(),
		divisions: cachedDivisions,
	}

	return { selection, competitionData }
}
